import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Button,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { dummyPackages } from '../../data/packages';
import PlanConfirmation from './plan_confirmation';
import CompanyInformation from './company_information';
import ReviewRegistration from './review_registration';
import Confirmation from './confirmation';
import { registerUser } from '../../network/auth';
import { registerCompany } from '../../network/company';
import { validateUKPhoneNumber } from '../../utilities/validator';
import { generateCognitoPassword } from './company_information/random_password_generator';
import ConfirmDialog from '../../ui-component/confirm_dialog';

const RegistrationPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);

  const [formData, setFormData] = useState({
    userId: '',
    companyName: '',
    companyType: 'Private Limited Company',
    sicCodes: [],
    registeredAddress: {
      street: '',
      city: '',
      postalCode: '',
      country: 'United Kingdom',
      isPublic: false,
    },
    initialOfficers: [],
    shareStructure: {
      shareClasses: [
        {
          type: 'ordinary',
          currency: 'GBP',
          votingRights: true,
          valuePerShare: 1,
          totalShares: 100,
        },
      ],
      statementOfCapital: {
        totalAmount: 100,
        totalShares: 100,
      },
    },
    payment: {
      amount: 150,
      status: 'Completed',
      date: '',
    },
    name: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [success, setSuccess] = useState(false);

  const validateForm = (activeStep, formData) => {
    const newErrors = {};
    if (activeStep === 1) {
      // Company name validation
      if (
        !formData.companyName ||
        formData.companyName.trim() === '' ||
        formData.companyName.trim() === 'LTD' ||
        formData.companyName.trim() === 'Limited'
      ) {
        newErrors.companyName = 'Valid company name is required';
      }

      // SIC codes validation
      if (!formData.sicCodes || formData.sicCodes.length === 0) {
        newErrors.sicCodes = 'At least one SIC code is required';
      }

      // Registered address validation
      if (!formData.registeredAddress?.street?.trim()) {
        newErrors['registeredAddress.street'] =
          'Street is required in the registered address.';
      }

      if (!formData.registeredAddress?.city?.trim()) {
        newErrors['registeredAddress.city'] =
          'City is required in the registered address.';
      }

      if (!formData.registeredAddress?.postalCode?.trim()) {
        newErrors['registeredAddress.postalCode'] =
          'Postal Code is required in the registered address.';
      }

      // User data - email, phone.
      if (!formData.email || formData.email.trim() === '') {
        newErrors.email = 'User email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Enter a valid email address';
      }

      if (!validateUKPhoneNumber(formData.phone)) {
        newErrors.phone = 'Enter a valid phone number';
      }

      if (!formData.name || formData.name.trim() === '') {
        newErrors.name = 'Enter a valid name for contact.';
      }

      // Officer role counts
      const officers = formData?.initialOfficers || [];
      const roleCounts = {
        director: 0,
        secretary: 0,
        'designated member': 0,
      };

      officers.forEach((officer, index) => {
        const role = officer.role?.toLowerCase();
        if (Object.prototype.hasOwnProperty.call(roleCounts, role)) {
          roleCounts[role]++;
        }

        // Validate individual officer fields
        if (!officer.name?.trim()) {
          newErrors[`directors.${index}.name`] = 'Officer name is required';
        }
      });

      // Role-based validation based on company type
      const companyType = formData.companyType;
      if (
        companyType === 'Private Limited Company' &&
        roleCounts.director < 1
      ) {
        newErrors.directors = 'At least one director is required';
      } else if (
        companyType === 'Public Limited Company' &&
        (roleCounts.director < 2 || roleCounts.secretary < 1)
      ) {
        newErrors.directors =
          'At least two directors and one secretary are required';
      } else if (
        companyType === 'Limited Liability Partnership' &&
        roleCounts['designated member'] < 2
      ) {
        newErrors.directors = 'At least two designated members are required';
      }

      console.log('Errors ', newErrors);
    }
    return newErrors;
  };

  const handleRegisterCompany = async () => {
    setLoading(true);
    setErrors(null);

    try {
      if (formData.userId) {
        // Add the company details to database
        const registerCompanyResp = await registerCompany(formData);
        if (!registerCompanyResp.companyNumber) {
          throw new Error(
            registerCompanyResp.message || 'Company registration failed'
          );
        }

        // 3. Send XML registration request
        const xmlData = buildXMLRequest();
        const response = await sendRegistrationRequest(xmlData);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Registration request failed');
        }

        setSuccess(true);
        handleNext();
      } else {
        console.error('Invalid user id!');
      }
    } catch (err) {
      let errorMessage = 'Registration failed. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Special handling for Cognito errors
      if (err?.errorDetails?.code === 'InvalidPhoneNumberException') {
        errorMessage =
          'Invalid phone number format. Please use format like +1234567890';
      } else if (err?.errorDetails?.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists';
      }

      setErrors(errorMessage);

      // Log the full error for debugging
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const plan = dummyPackages.find((p) => p.id === planId);
  if (!plan) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h6" color="error">
          Plan not found. Please go back and select a valid plan.
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Container>
    );
  }

  const steps = [
    'Plan Confirmation',
    'Company Details',
    'Review & Submit',
    'Confirmation',
  ];

  const handleNext = async () => {
    if (activeStep === 1) {
      const validationErrors = validateForm(activeStep, formData);

      const hasErrors = Object.keys(validationErrors).length > 0;
      if (hasErrors) {
        setErrors(validationErrors);
        return;
      } else {
        setErrors(null);
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const processRegistration = async () => {
  //   const users = await getUsers();
  //   const doesUserExists = users?.some(
  //     (user) => user.email === formData?.email
  //   );

  //   if (doesUserExists) setOpenConfirm(true);
  //   else submitRegistration();
  // };

  const submitRegistration = async () => {
    setLoading(true);
    setErrors(null);

    try {
      const resp = await registerUser(
        formData?.email,
        formData?.name,
        generateCognitoPassword(),
        formData?.phone,
        false
      );

      if (!resp?.user?.id) {
        throw new Error(resp.message || 'User registration failed');
      }

      formData.userId = resp?.user?.id;
      if (resp?.user?.alredyExists) {
        formData.name = resp?.user?.name;
        formData.phone = resp?.user?.phone;
        setOpenConfirm(true);
      } else {
        await handleRegisterCompany();
      }
    } catch (err) {
      let errorMessage = 'Registration failed. Please try again.';

      // Handle different error scenarios
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Special handling for Cognito errors
      if (err?.errorDetails?.code === 'InvalidPhoneNumberException') {
        errorMessage =
          'Invalid phone number format. Please use format like +1234567890';
      } else if (err?.errorDetails?.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists';
      }

      setErrors(errorMessage);

      // Log the full error for debugging
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildXMLRequest = () => {
    return ` 
<CompanyRegistration>
  <Plan>${plan.name}</Plan>
  <Price>${plan.price}</Price>
  <Company>
    <Name>${formData.companyName}</Name>
    <Email>${formData.email}</Email>
    <Phone>${formData.phone}</Phone>
    <Address>${formData.address}</Address>
  </Company>
  <Timestamp>${new Date().toISOString()}</Timestamp>
</CompanyRegistration>
    `.trim();
  };

  const sendRegistrationRequest = async (xml) => {
    console.log('Submitting XML:', xml);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ ok: true });
      }, 1500);
    });
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <PlanConfirmation plan={plan} />;
      case 1:
        return (
          <CompanyInformation
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
          />
        );
      case 2:
        return (
          <ReviewRegistration plan={plan} formData={formData} errors={errors} />
        );
      case 3:
        return <Confirmation />;
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  //const isSubmitDisabled = !isValidForm(activeStep, formData) || loading;

  return (
    <Container sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Plans
      </Button>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step
            key={label}
            sx={{
              '& .MuiStepLabel-label': { fontSize: '1.1rem' },
              '& .MuiStepIcon-root': { fontSize: '2rem' },
            }}
          >
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: '400px' }}>{getStepContent(activeStep)}</Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        {activeStep !== steps.length - 1 && (
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
        )}
        {activeStep !== steps.length - 1 && (
          <Button
            variant="contained"
            onClick={
              activeStep === steps.length - 2 ? submitRegistration : handleNext
            }
            //disabled={isSubmitDisabled}
          >
            {loading
              ? 'Processing...'
              : activeStep === steps.length - 2
                ? 'Submit Registration'
                : 'Next'}
          </Button>
        )}
      </Box>
      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ConfirmDialog
        open={openConfirm}
        title="Confirm Registration"
        content={`You already have an account with us using this email. Would you like to register this company with your existing account?`}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={() => {
          setOpenConfirm(false);
          handleRegisterCompany();
        }}
      />
    </Container>
  );
};

export default RegistrationPage;
